import type { PropsOf } from '@emotion/react';
import type FacebookTableSearchFilter from 'components/FacebookTable/FacebookTableSearchFilter';
import * as date from 'date-fns';
import { useAdManager, useQuery } from 'hooks';
import _ from 'lodash';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';
import { useAdAccountStore } from 'stores';
import type { Ad } from 'types/fbData/ad';
import type { Adset } from 'types/fbData/adset';
import type { Campaign } from 'types/fbData/campaign';
import {
  type AdData,
  type AdsetData,
  type CampaignData,
  type FBTableTabs,
  PAGE_DEFAULT_SIZE,
  type PageSettings,
  type PaginationSettings,
  type StateDataSummary
} from 'types/fbTable';
import type { IApiResponse } from 'types/hooks/useHandleApi';
import type { MetaAdAccount } from 'types/stores/ad-account-store';

type PaginateOptions = {
  whichTab: FBTableTabs;
  direction: 1 | -1;
  opts?: PaginationSettings;
};

/* rows only need ids here; FBTable passes back the full row objects */
type SelectedRow = { id: string };

type PaginatedTabData = {
  campaigns?: Campaign[];
  adsets?: Adset[];
  ads?: Ad[];
  pageSize: number;
  pageNumber: number;
  summary?: StateDataSummary;
};

type GetMethods = {
  [key in FBTableTabs]?: (
    opts: PaginationSettings,
    adAccountId: string
  ) => Promise<IApiResponse<PaginatedTabData>>;
};

type AdManagerNavigationOpts = {
  skipLastUpdate?: boolean;
  initialFilters?: Record<string, string | string[]>;
};

type DateRange = PropsOf<typeof FacebookTableSearchFilter>['dateRange'];

type QueryStringParams = 'startDate' | 'endDate' | 'brandId';

const yesterday = date.startOfDay(date.endOfYesterday());
const on90DaysAgo = date.sub(yesterday, { days: 89 });
const DEFAULT_DATE_RANGE = { endDate: yesterday, startDate: on90DaysAgo };

const getInitialDateRange = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return DEFAULT_DATE_RANGE;
  const start = date.parse(startDate, 'yyyy-MM-dd', new Date());
  const end = date.parse(endDate, 'yyyy-MM-dd', new Date());
  return { startDate: start, endDate: end };
};

const useAdManagerNavigation = (
  tab: FBTableTabs,
  getMethods: GetMethods,
  { skipLastUpdate = false, initialFilters = {} }: AdManagerNavigationOpts = {}
) => {
  const { getQueryParams } = useQuery();
  const { metaAdAccountsList } = useAdAccountStore();
  const { startDate, endDate, brandId } = getQueryParams<QueryStringParams>();
  const { getLastUpdatedAt, loading: adManagerLoading } = useAdManager();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [pages, setPages] = useState<Record<FBTableTabs, PageSettings>>({
    campaigns: {
      hasNext: true,
      hasPrevious: false,
      page: 0
    },
    ads: {
      hasNext: true,
      hasPrevious: false,
      page: 0
    },
    adsets: {
      hasNext: true,
      hasPrevious: false,
      page: 0
    }
  });
  const [lastUpdate, setLastUpdate] = useState<string>();
  const [selectedCampaigns, setSelectedCampaigns] = useState<SelectedRow[]>([]);
  const [selectedAdsets, setSelectedAdsets] = useState<SelectedRow[]>([]);
  const [selectedAds, setSelectedAds] = useState<SelectedRow[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignData>({ data: [], summary: undefined });
  const [adsets, setAdsets] = useState<AdsetData>({ data: [], summary: undefined });
  const [ads, setAds] = useState<AdData>({ data: [], summary: undefined });
  const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange(startDate, endDate));
  const [previousDates, setPreviousDates] = useState<typeof dateRange>(dateRange);

  // default to first ad account
  const [metaAdAccount, setMetaAdAccount] = useState<MetaAdAccount | null>(
    metaAdAccountsList.at(0) ?? null
  );

  const setSelected = {
    campaigns: setSelectedCampaigns,
    adsets: setSelectedAdsets,
    ads: setSelectedAds
  };

  const selected = {
    campaigns: selectedCampaigns,
    adsets: selectedAdsets,
    ads: selectedAds
  };

  const getSelectedItems = (whichTab: FBTableTabs) => {
    const checkIfCampaignShouldRequestData = () => {
      if (JSON.stringify(previousDates) !== JSON.stringify(dateRange)) {
        setPreviousDates(dateRange);
        return {};
      }
      return undefined;
    };
    const checkIfShouldPaginate = (whichToVerify: FBTableTabs) => {
      const selectedRows = selected[whichToVerify];
      if (selectedRows.length) {
        const ids = selectedRows.map(item => item.id);
        const tabKey = pluralize.singular(whichToVerify);
        return { [`${tabKey}Ids`]: ids } as Record<string, string[]>;
      }
      // TODO(Jenky): This logic shouldn't unselect already selected in tab
      return {};
    };

    if (whichTab === 'campaigns') return checkIfCampaignShouldRequestData();
    if (whichTab === 'adsets') return checkIfShouldPaginate('campaigns');
    if (whichTab === 'ads') {
      // I liked this better :c... I guess figure out previous TODO first :\
      // return checkIfShouldPaginate('adsets') ?? checkIfShouldPaginate('campaigns');
      const queryItems = checkIfShouldPaginate('adsets');
      if (Object.keys(queryItems).length) return queryItems;
      return checkIfShouldPaginate('campaigns');
    }
    return undefined;
  };

  const onPaginate = async ({ whichTab, direction, opts }: PaginateOptions) => {
    if (!metaAdAccount?.adAccountId) return;

    const method = getMethods[whichTab];
    if (!method) return;
    setLoading(true);
    const pageSetting = pages[whichTab];
    const page = opts?.pageNumber ?? pageSetting.page + direction;
    const queryItems = { ...opts?.queryItems };

    if (dateRange.startDate && !queryItems.startDate) {
      queryItems.startDate = date.format(dateRange.startDate, 'yyyy-MM-dd');
    }
    if (dateRange.endDate && !queryItems.endDate) {
      queryItems.endDate = date.format(dateRange.endDate, 'yyyy-MM-dd');
    }

    const response = await method({ pageNumber: page, queryItems }, metaAdAccount.adAccountId);

    const data = response.data as PaginatedTabData;
    const { pageSize, pageNumber, summary } = data;

    const tabSettings = {
      hasNext: pageSize === PAGE_DEFAULT_SIZE,
      hasPrevious: pageNumber > 1,
      page
    };

    setPages(allPages => ({ ...allPages, [whichTab]: tabSettings }));

    if (whichTab === 'campaigns') setCampaigns({ data: data.campaigns ?? [], summary });
    else if (whichTab === 'adsets') setAdsets({ data: data.adsets ?? [], summary });
    else setAds({ data: data.ads ?? [], summary });
    setLoading(false);
  };

  const onPaginateNext = async (whichTab: FBTableTabs, queryOpts?: PaginationSettings) => {
    const queryItems = getSelectedItems(whichTab);
    onPaginate({ whichTab, direction: 1, opts: _.merge({ queryItems }, queryOpts) });
  };

  const onPaginatePrevious = async (whichTab: FBTableTabs) => {
    const queryItems = getSelectedItems(whichTab);
    onPaginate({ whichTab, direction: -1, opts: { queryItems } });
  };

  const onRowSelect = (whichTab: FBTableTabs, selectedRows: SelectedRow[]) => {
    setSelected[whichTab](selectedRows);
  };

  const onDateRangeChange = (newDateRange: typeof dateRange) => {
    setDateRange(newDateRange);
  };

  const onSelectAdAccount = (adAccountId: string) => {
    const adAccount = metaAdAccountsList.find(account => account.adAccountId === adAccountId);
    if (!adAccount) return;
    setMetaAdAccount(adAccount);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const getLastUpdate = async () => {
      const updatedAt = await getLastUpdatedAt();

      if (updatedAt) setLastUpdate(updatedAt);
    };
    const initialize = async () => {
      /* TODO(Jenky): might remove from here, use intialFilters instead */
      const initialQueryItems = new Map();
      if (brandId) initialQueryItems.set('brandId', brandId);
      await onPaginateNext(tab, {
        queryItems: { ...Object.fromEntries(initialQueryItems), ...initialFilters }
      });
      if (!skipLastUpdate) await getLastUpdate();
      setLoaded(true);
    };
    initialize();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (!loaded) return;
    const queryItems = getSelectedItems(tab);
    if (queryItems) {
      onPaginate({
        direction: 1,
        whichTab: tab,
        opts: {
          pageNumber: 1,
          queryItems
        }
      });
    }
  }, [tab]);

  // on date change, request new data for that date range on the current tab
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (loaded) {
      // include selected items in query
      const queryItems = getSelectedItems(tab);
      onPaginate({ whichTab: tab, direction: 1, opts: { pageNumber: 1, queryItems } });
    }
  }, [dateRange, metaAdAccount?.adAccountId]);

  const isLoading = loading || adManagerLoading;

  return {
    loading: isLoading,
    dateRange,
    loaded,
    lastUpdate,
    campaigns,
    adsets,
    ads,
    onPaginateNext,
    onPaginatePrevious,
    onRowSelect,
    onDateRangeChange,
    onSelectAdAccount,
    metaAdAccount,
    pages
  };
};

export default useAdManagerNavigation;
